import React, {useEffect, useState} from 'react'
import {
    Button,
    Chip,
    Collapse,
    FormControl,
    FormControlLabel,
    FormLabel,
    LinearProgress,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material"
import {ProjectJson} from "../../api/json/ProjectJson"
import TransformJson from "../../api/json/TransformJson"
import QualityCheckJson from "../../api/json/QualityCheckJson"
import AsyncButton from "../button/AsyncButton"
import ApiError from "../../api/ApiError"
import ErrorMessage from "../error/ErrorMessage"
import CompletionOptionsForm from "../completions/CompletionOptionsForm"
import GroupedModelSelect from "../models/GroupedModelSelect"
import {AnyModelSpecJson} from "../../api/json/ModelSpecJson"
import {CompletionOptionsJson, DEFAULT_COMPLETION_OPTIONS} from "../../api/json/CompletionOptionsJson"
import {ScoringMethod, resolveNameForScoringMethod} from "../../api/enum/ScoringMethod"
import {TransformQualityCheckRequests} from "../../api/requests/TransformQualityCheckRequests"
import {QualityCheckRequests} from "../../api/requests/QualityCheckRequests"
import {ColumnType} from "../../api/enum/ColumnType"
import {useToasts} from "../../hooks/useToasts"

interface TransformQualityCheckFormProps {
    project: ProjectJson
    transform: TransformJson
}

function TransformQualityCheckForm({project, transform}: TransformQualityCheckFormProps) {
    const toasts = useToasts()

    const [qualityCheck, setQualityCheck] = useState<QualityCheckJson | null>(null)
    const [loading, setLoading] = useState(true)
    const [running, setRunning] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [error, setError] = useState<ApiError>()

    const [expectedOutputColumnId, setExpectedOutputColumnId] = useState<string>("")
    const [model, setModel] = useState<AnyModelSpecJson>()
    const [scoringMethod, setScoringMethod] = useState<ScoringMethod>(ScoringMethod.PREDICTIVE)
    const [completionOptions, setCompletionOptions] = useState<CompletionOptionsJson>({
        ...DEFAULT_COMPLETION_OPTIONS,
        numberOfCompletions: 1,
    })
    const [showAdvanced, setShowAdvanced] = useState(false)

    const textColumns = project.columns.filter(c => c.type === ColumnType.TEXT)

    useEffect(() => {
        loadLatestQualityCheck()
    }, [transform.id])

    useEffect(() => {
        if (!qualityCheck || qualityCheck.completed) return

        const interval = setInterval(() => {
            QualityCheckRequests.get(qualityCheck.id)
                .then(setQualityCheck)
                .catch(toasts.showError)
        }, 2000)

        return () => clearInterval(interval)
    }, [qualityCheck?.id, qualityCheck?.completed])

    const loadLatestQualityCheck = () => {
        setLoading(true)
        TransformQualityCheckRequests.list(project.id, transform.id)
            .then(response => {
                setQualityCheck(response.content.length > 0 ? response.content[0] : null)
            })
            .catch(toasts.showError)
            .finally(() => setLoading(false))
    }

    const handleCreate = () => {
        if (!model || !expectedOutputColumnId) return

        setRunning(true)
        setError(undefined)
        TransformQualityCheckRequests.create(project.id, transform.id, {
            expectedOutputColumnId,
            completionPlatform: model.platform,
            completionModelName: model.name,
            scoringMethod,
            temperature: completionOptions.temperature,
            topP: completionOptions.topP,
            presencePenalty: completionOptions.presencePenalty,
            frequencyPenalty: completionOptions.frequencyPenalty,
            maxTokens: completionOptions.maxTokens,
        })
            .then(setQualityCheck)
            .catch(setError)
            .finally(() => setRunning(false))
    }

    const handleDelete = () => {
        if (!qualityCheck) return

        setDeleting(true)
        TransformQualityCheckRequests.delete(project.id, transform.id, qualityCheck.id)
            .then(() => setQualityCheck(null))
            .catch(toasts.showError)
            .finally(() => setDeleting(false))
    }

    if (loading) return null

    if (qualityCheck) {
        const stats = qualityCheck.stats
        const inProgress = !qualityCheck.completed
        const totalRows = stats.completedTestRuns

        return (
            <Stack spacing={2}>
                {inProgress && totalRows > 0 &&
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="body2">In progress</Typography>
                        <LinearProgress variant="indeterminate" sx={{width: "100%", maxWidth: 300}}/>
                        <Typography variant="caption" color="text.secondary">
                            {stats.completedTestRuns} completed
                        </Typography>
                    </Stack>
                }

                {!inProgress &&
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip label={`${stats.completedTestRuns} test runs`} size="small" variant="outlined"/>
                        {stats.scoredTestRuns === stats.completedTestRuns && stats.completedTestRuns > 0 &&
                            <Chip label={`${stats.score}% avg score`} size="small" color="primary" variant="outlined"/>
                        }
                        {stats.averageLatency != null && stats.averageLatency > 0 &&
                            <Chip label={`${stats.averageLatency} ms`} size="small" variant="outlined"/>
                        }
                    </Stack>
                }

                <Stack direction="row" spacing={1}>
                    <AsyncButton variant="outlined"
                                 color="secondary"
                                 size="small"
                                 waiting={deleting}
                                 onClick={handleDelete}>
                        Delete & restart
                    </AsyncButton>
                </Stack>
            </Stack>
        )
    }

    return (
        <Stack spacing={3}>
            <FormControl fullWidth size="small">
                <FormLabel sx={{mb: 1}}>Expected output column</FormLabel>
                <Select
                    value={expectedOutputColumnId}
                    onChange={e => setExpectedOutputColumnId(e.target.value)}
                    displayEmpty
                >
                    <MenuItem value="" disabled>Select a column</MenuItem>
                    {textColumns.map(col => (
                        <MenuItem key={col.id} value={col.id}>{col.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <GroupedModelSelect value={model} onChange={setModel} instructModelsOnly label="Platform & Base Model"/>

            <FormControl>
                <FormLabel sx={{mb: 1}}>Scoring method</FormLabel>
                <RadioGroup
                    value={scoringMethod}
                    onChange={e => setScoringMethod(e.target.value as ScoringMethod)}
                >
                    {Object.values(ScoringMethod).map(method => (
                        <FormControlLabel
                            key={method}
                            value={method}
                            control={<Radio size="small"/>}
                            label={resolveNameForScoringMethod(method)}
                        />
                    ))}
                </RadioGroup>
            </FormControl>

            <Typography variant="body2"
                        color="primary"
                        sx={{cursor: 'pointer', userSelect: 'none'}}
                        onClick={() => setShowAdvanced(a => !a)}>
                {showAdvanced ? "Hide advanced options" : "Show advanced options"}
            </Typography>
            <Collapse in={showAdvanced} unmountOnExit>
                {model &&
                    <CompletionOptionsForm
                        platform={model.platform}
                        hideNumberOfCompletions
                        hideAdvanced={false}
                        onChange={setCompletionOptions}
                    />
                }
            </Collapse>

            {error && <ErrorMessage error={error}/>}

            <AsyncButton variant="contained"
                         waiting={running}
                         disabled={!model || !expectedOutputColumnId}
                         onClick={handleCreate}>
                Run quality check
            </AsyncButton>
        </Stack>
    )
}

export default TransformQualityCheckForm
